import {
  ArrowBendDownRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { OutlineInstruction } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { WorkbookInstructionEditor } from './WorkbookInstructionEditor';
import { WorkbookGenerateStepButton } from './WorkbookGeneration';
import { useWorkbookProcess } from './useWorkbookProcess';
import { instructionSummary } from './useExecutionLabel';
import { IconTooltip } from '@/components/common/IconTooltip';

/** The open editor for one Instruction: its text, title and Outline ops. */
export function WorkbookProcessInstruction({
  node,
}: {
  node: OutlineInstruction;
}) {
  const gt = useGT();
  const {
    outline,
    references,
    canUpdate,
    edits: workbookEdits,
    generation,
    setEditingId,
    insert,
    renderStepActions,
  } = useWorkbookProcess();
  const { id, step } = node;
  const edits = workbookEdits.step(id);
  const { generating } = generation;
  return (
    <div className="space-y-2">
      {canUpdate && (
        <div className="flex flex-wrap items-center gap-1">
          <IconTooltip>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              disabled={
                generating ||
                !outline.can({ type: 'move', id, direction: 'up' })
              }
              aria-label={gt('Move instruction up', {
                context: 'workbook notebook reorder instruction',
              })}
              onClick={() => edits.move('up')}
            >
              <ArrowUpIcon />
            </Button>
          </IconTooltip>
          <IconTooltip>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              disabled={
                generating ||
                !outline.can({ type: 'move', id, direction: 'down' })
              }
              aria-label={gt('Move instruction down', {
                context: 'workbook notebook reorder instruction',
              })}
              onClick={() => edits.move('down')}
            >
              <ArrowDownIcon />
            </Button>
          </IconTooltip>
          <IconTooltip>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              disabled={generating || !outline.can({ type: 'outdent', id })}
              aria-label={gt('Outdent instruction', {
                context: 'workbook process nesting',
              })}
              onClick={() => edits.outdent()}
            >
              <ArrowLeftIcon />
            </Button>
          </IconTooltip>
          <IconTooltip>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              disabled={generating || !outline.can({ type: 'indent', id })}
              aria-label={gt('Indent instruction', {
                context: 'workbook process nesting',
              })}
              onClick={() => edits.indent()}
            >
              <ArrowRightIcon />
            </Button>
          </IconTooltip>
          <Button
            size="sm"
            variant="ghost"
            disabled={generating || outline.full}
            onClick={() => insert(id)}
          >
            <ArrowBendDownRightIcon />
            <T context="workbook add child instruction">Add nested step</T>
          </Button>
          <span className="flex-1" />
          {/* A draft or a Data note can go; a Worksheet's Instruction describes it. */}
          {(!step.itemIds.length || outline.dataItemOf(id)) && (
            <IconTooltip>
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                disabled={generating}
                aria-label={gt('Discard', {
                  context: 'workbook remove draft instruction',
                })}
                onClick={() => edits.remove()}
              >
                <TrashIcon />
              </Button>
            </IconTooltip>
          )}
          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
            <CheckIcon />
            <T context="workbook finish editing instructions">Done</T>
          </Button>
          <WorkbookGenerateStepButton id={id} step={step} variant="outline" />
          {renderStepActions?.(id)}
        </div>
      )}
      {canUpdate && (
        <Input
          aria-label={gt('Step name', {
            context: 'workbook instruction title',
          })}
          autoFocus
          disabled={generating}
          maxLength={200}
          placeholder={gt('Step name', {
            context: 'workbook instruction title',
          })}
          value={step.title ?? instructionSummary(step, references)}
          onChange={(event) => edits.update({ title: event.target.value })}
        />
      )}
      {canUpdate && (
        <p className="text-xs text-muted-foreground">
          <T context="workbook instruction save and apply help">
            Edits save automatically. Generate applies this instruction to the
            workbook; Done closes the editor.
          </T>
        </p>
      )}
      <WorkbookInstructionEditor
        value={step.instructions}
        readOnly={!canUpdate || generating}
        mentionSources={references.mentionSources}
        label={gt('Step instruction', {
          context: 'workbook process step editor',
        })}
        onChange={(instructions) => {
          if (canUpdate && !generating) edits.update({ instructions });
        }}
      />
    </div>
  );
}
